import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import api from '../lib/api';
import {
  Search, Trash2, Eye, Clock, FileImage, Loader2, ImageOff,
  Download, UserRound, Layers
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { UrgencyBadge, EmergencyFlagBadge } from '../components/UrgencyBadge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const HistoryPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEmergency, setFilterEmergency] = useState('ALL');
  const [filterLevel, setFilterLevel] = useState('ALL');
  const [filterModality, setFilterModality] = useState('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sort, setSort] = useState('date');
  const [deleteId, setDeleteId] = useState(null);
  const [total, setTotal] = useState(0);
  const [exporting, setExporting] = useState(false);

  const fetchAnalyses = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterEmergency !== 'ALL') params.append('emergency_flag', filterEmergency);
      if (filterLevel !== 'ALL') params.append('emergency_level', filterLevel);
      if (filterModality !== 'ALL') params.append('modality', filterModality);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      params.append('sort', sort);
      params.append('limit', '50');

      const res = await api.get(`/analyses?${params.toString()}`);
      setAnalyses(res.data.analyses || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error('Failed to load analyses:', error);
      toast.error(t('errors.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [search, filterEmergency, filterLevel, filterModality, dateFrom, dateTo, sort, t]);

  useEffect(() => {
    const debounce = setTimeout(fetchAnalyses, 300);
    return () => clearTimeout(debounce);
  }, [fetchAnalyses]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/analyses/${deleteId}`);
      setAnalyses((prev) => prev.filter((a) => a.id !== deleteId));
      setTotal((prev) => prev - 1);
      toast.success(t('history.confirm_delete'));
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(t('errors.load_failed'));
    } finally {
      setDeleteId(null);
    }
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get('/analyses/export/csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'analyses_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV');
    } catch (error) {
      console.error('CSV export error:', error);
      toast.error(t('errors.load_failed'));
    } finally {
      setExporting(false);
    }
  };

  const criticalBorder = (level) =>
    level === 'CRITICAL'
      ? 'border-l-[3px] border-l-red-500'
      : level === 'HIGH'
        ? 'border-l-[3px] border-l-orange-500'
        : '';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold">{t('history.title')}</h2>
          <p className="text-sm text-muted-foreground mt-1">{t('history.subtitle')}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExportCsv}
          disabled={exporting}
          className="text-xs gap-1.5"
          data-testid="export-csv-button"
        >
          {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          {t('history.export_csv')}
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-[hsl(210,18%,9%)] border-border mb-6">
        <CardContent className="py-4 space-y-3">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('history.search')}
                className="pl-9 h-9 text-sm bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]"
                data-testid="history-search-input"
              />
            </div>

            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="h-9 w-[170px] text-xs bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]" data-testid="history-sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(210,18%,10%)] border-border">
                <SelectItem value="date" className="text-xs">{t('history.sort_date')}</SelectItem>
                <SelectItem value="triage" className="text-xs">{t('history.sort_triage')}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterEmergency} onValueChange={setFilterEmergency}>
              <SelectTrigger className="h-9 w-[150px] text-xs bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]" data-testid="history-filter-urgency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(210,18%,10%)] border-border">
                <SelectItem value="ALL" className="text-xs">{t('history.all')} - {t('history.filter_emergency')}</SelectItem>
                <SelectItem value="YES" className="text-xs">YES</SelectItem>
                <SelectItem value="NO" className="text-xs">NO</SelectItem>
                <SelectItem value="UNCERTAIN" className="text-xs">UNCERTAIN</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterLevel} onValueChange={setFilterLevel}>
              <SelectTrigger className="h-9 w-[150px] text-xs bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]" data-testid="history-filter-level">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(210,18%,10%)] border-border">
                <SelectItem value="ALL" className="text-xs">{t('history.all')} - {t('history.filter_level')}</SelectItem>
                <SelectItem value="CRITICAL" className="text-xs">CRITICAL</SelectItem>
                <SelectItem value="HIGH" className="text-xs">HIGH</SelectItem>
                <SelectItem value="MODERATE" className="text-xs">MODERATE</SelectItem>
                <SelectItem value="LOW" className="text-xs">LOW</SelectItem>
                <SelectItem value="NONE" className="text-xs">NONE</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterModality} onValueChange={setFilterModality}>
              <SelectTrigger className="h-9 w-[160px] text-xs bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]" data-testid="history-filter-modality">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(210,18%,10%)] border-border">
                <SelectItem value="ALL" className="text-xs">{t('history.all')} - {t('history.filter_modality')}</SelectItem>
                <SelectItem value="Radiographie" className="text-xs">X-Ray / Radiographie</SelectItem>
                <SelectItem value="TDM" className="text-xs">CT / TDM</SelectItem>
                <SelectItem value="IRM" className="text-xs">MRI / IRM</SelectItem>
                <SelectItem value="Autre" className="text-xs">Other / Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground uppercase">{t('history.date_from')}</span>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-8 w-[150px] text-xs bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]"
                data-testid="history-date-from"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-muted-foreground uppercase">{t('history.date_to')}</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-8 w-[150px] text-xs bg-[hsl(210,18%,7%)] border-[hsl(210,14%,18%)]"
                data-testid="history-date-to"
              />
            </div>
            {(dateFrom || dateTo) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setDateFrom(''); setDateTo(''); }}
                className="h-8 text-xs text-muted-foreground"
              >
                ✕
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={28} className="text-cyan-400 animate-spin" />
        </div>
      ) : analyses.length === 0 ? (
        <Card className="bg-[hsl(210,18%,9%)] border-border">
          <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
            <ImageOff size={48} className="text-muted-foreground/40" />
            <div className="text-center">
              <p className="text-sm text-muted-foreground">{t('history.no_results')}</p>
              <p className="text-xs text-muted-foreground/60 mt-1">{t('history.no_results_hint')}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/')}
              className="mt-2"
            >
              {t('nav.upload')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground mb-2">{total} analyses</p>
          {analyses.map((analysis, index) => (
            <motion.div
              key={analysis.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, delay: Math.min(index * 0.04, 0.4) }}
            >
              <Card
                className={`bg-[hsl(210,18%,9%)] border-border hover:border-[hsl(210,14%,25%)] transition-colors duration-200 cursor-pointer ${criticalBorder(analysis.emergency_level)}`}
                onClick={() => navigate(`/analysis/${analysis.id}`)}
                data-testid={`history-card-${analysis.id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-[hsl(210,14%,14%)] flex items-center justify-center shrink-0">
                      <FileImage size={18} className="text-cyan-400" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <UrgencyBadge level={analysis.emergency_level || 'MODERATE'} size="sm" />
                        <EmergencyFlagBadge flag={analysis.emergency_flag || 'UNCERTAIN'} />
                        {analysis.num_images > 1 && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-cyan-300 bg-cyan-500/10 px-1.5 py-0.5 rounded">
                            <Layers size={9} />
                            {analysis.num_images}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                        <span>{analysis.modality_guess || 'Unknown'}</span>
                        <span>•</span>
                        <span>{analysis.region || 'Unknown'}</span>
                        {analysis.user_name && (
                          <>
                            <span>•</span>
                            <span className="inline-flex items-center gap-1">
                              <UserRound size={10} className="text-cyan-400" />
                              {analysis.user_name}
                            </span>
                          </>
                        )}
                        {analysis.clinical_context && (
                          <>
                            <span>•</span>
                            <span className="truncate max-w-[180px]">{analysis.clinical_context}</span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-right hidden sm:block">
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock size={10} />
                          {analysis.created_at ? new Date(analysis.created_at).toLocaleDateString() : ''}
                        </p>
                        <p className="text-[10px] text-muted-foreground/60">
                          {analysis.created_at ? new Date(analysis.created_at).toLocaleTimeString() : ''}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/analysis/${analysis.id}`);
                        }}
                        data-testid={`view-analysis-${analysis.id}`}
                      >
                        <Eye size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-muted-foreground hover:text-red-400"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteId(analysis.id);
                        }}
                        data-testid={`delete-analysis-${analysis.id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-[hsl(210,18%,9%)] border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('history.delete_confirm')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('history.delete_confirm_desc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[hsl(210,14%,14%)] border-border">
              {t('history.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              {t('history.confirm_delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};

export default HistoryPage;
